/**
 * PATH: packages/email-hub/src/feature/pages/inbox-page/InboxListPane.tsx
 * FILE_ID: EMAIL-INBOX-LIST-PANE-001
 * PURPOSE: Assemble the command, analysis, and result surfaces.
 */

import type { MouseEvent } from 'react'
import { cn } from '../../../_internal/lib/utils'
import { KeyboardShortcutsPanel } from '../../components/KeyboardShortcutsPanel'
import type { Email } from '../../hooks/useEmails'
import { InboxAnalysisSurface } from './InboxAnalysisSurface'
import { InboxCommandSurface } from './InboxCommandSurface'
import { InboxResultsSurface } from './InboxResultsSurface'
import { InboxStatusSurface } from './InboxStatusSurface'
import type { InboxPageController } from './useInboxPageController'

interface InboxListPaneProps {
  vm: InboxPageController
  handleContextMenu: (event: MouseEvent, email: Email) => void
}

export function InboxListPane({ vm, handleContextMenu }: InboxListPaneProps) {
  const listVisible =
    (!vm.isNarrow || (!vm.selectedEmail && !vm.isComposeMode))
    && !(
      vm.readerExpanded
      && (vm.selectedEmail || vm.isComposeMode)
      && !vm.isNarrow
    )
  return (
    <div
      hidden={!listVisible}
      aria-hidden={!listVisible || undefined}
      className={cn(
        'flex flex-col h-full min-w-0 border-border',
        !vm.isNarrow && vm.inboxSide === 'right'
          ? 'border-l order-3'
          : 'border-r',
        !vm.isNarrow && vm.inboxSide !== 'right' && 'order-1',
        vm.isNarrow
          ? 'flex-1'
          : vm.selectedEmail || vm.isComposeMode
            ? 'shrink-0'
            : 'flex-1',
      )}
      style={{
        backgroundColor:
          'var(--email-pane-fill, var(--email-pane, transparent))',
        ...(!vm.isNarrow && (vm.selectedEmail || vm.isComposeMode)
          ? { width: vm.effectiveListWidth }
          : {}),
      }}
    >
      <InboxCommandSurface vm={vm} />
      <InboxStatusSurface vm={vm} />
      <InboxAnalysisSurface vm={vm} />
      {/* LowConfidenceDigest must NOT mount here. It stacked a second amber
          card above ParkedReviewDigest (operator 2026-07-26: "still bad").
          ParkedReviewDigest in InboxResultsSurface is the one AI digest. */}
      <InboxResultsSurface vm={vm} handleContextMenu={handleContextMenu} />
      <KeyboardShortcutsPanel
        open={vm.shortcutsPanelOpen}
        onClose={() => vm.setShortcutsPanelOpen(false)}
      />
    </div>
  )
}
